import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Signal, Type, computed } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MimeTypesEnum, ToastrActionEnum } from '../../../utils/enums';
import { IGetClass, IModalWindow, ISetOptions } from 'src/app/utils/interfaces/modal-dialog.interface';
import { ModalWindowsTypes } from 'src/app/core/enums';
import { SaveStatusEnum } from '../../../services/cloud-storage/enums/save-status.enum';
import { CloudDlgManagerService } from '../../../services/cloud-storage/cloud-dlg-manager.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FileCopyInfo } from '../../../services/cloud-storage/models/file-copy-info';
import { SettingsService } from '../../../services/settings/settings.service';
import { CloudServiceEnum } from '../../../services/cloud-storage/enums/cloud-service.enum';
import { CloudData } from '../../../services/cloud-storage/metadata/CloudData';
import { ICloudSettings, ISettings } from '../../../core/interfaces';
import { FileCopyService } from '../../../services/file-copy.service';
import { CurrentDialogEnum } from '../../../utils/enums/current-dialog.enum';
import { MatDialog } from '@angular/material/dialog';
import { AudioMakerService } from '../../../services/audio-maker/audio-maker.service';
import { BaseFileBrowserComponent } from '../../base-file-browser/base-file-browser.component';
import { CloudSettingsService } from 'src/app/services/cloud-settings.service';
import { CloudFoldersEnum } from 'src/app/utils/enums/cloud-folders.enum';
import { CloudRecentFilesService } from 'src/app/services/cloud-storage/cloud-recent-files.service';
import { FilenameHelper } from 'src/app/utils/helpers/filename.helper';
import { CloudSortingData, CloudSortingParam } from 'src/app/services/cloud-storage/cloud-sorter.service';
import { PaneService } from 'src/app/services/pane.service';
import { ExtraDocumentsRestrictionService } from 'src/app/services/restrictions/extra-documents-restriction.service';
// import { CommonMessageDialogComponent } from '../common-message-dialog/common-message-dialog.component';
import { CloudFavoriteFilesService } from 'src/app/services/cloud-storage/cloud-favorite-files.service';
import { ToastrDialogService } from 'src/app/services/toastr-dialog.service';
import { ExamPropertiesTabNames } from 'src/app/services/exam/enums/properties-tab-names';
import { RoundedNumberPipe } from '../../../utils/pipes/rounded-number.pipe';
import { MatMenuModule } from '@angular/material/menu';
import { TooltipDirective } from '../../../utils/directives/tooltip.directive';
import { PureSpinnerComponent } from '../../pure-spinner/pure-spinner.component';
import { CloudStorageDialogTableHeaderComponent } from '../cloud-storage-dialog-table-header/cloud-storage-dialog-table-header.component';
import { BreadcrumbsComponent } from '../../breadcrumbs/breadcrumbs.component';
import { NgClass, DatePipe } from '@angular/common';
import { FileNamePipe } from 'src/app/utils/pipes/file/file-name.pipe';
import { FileExtensionPipe } from 'src/app/utils/pipes/file/file-extension.pipe';
import { MessageDialogData, MessageDialogActionColor, MessageDialogComponent } from '../message-dialog/message-dialog.component';

// interface FilenameAndExtension {
//   filename: string;
//   extension: string;
// }

export interface CloudStorageDialogOptions {
  file: File | null;
  showAllFiles: boolean;
  dialogStatus: SaveStatusEnum;
}

@Component({
    selector: 'asg-cloud-storage-dialog',
    templateUrl: './cloud-storage-dialog.component.html',
    styleUrls: ['./cloud-storage-dialog.component.scss'],
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, BreadcrumbsComponent, CloudStorageDialogTableHeaderComponent, PureSpinnerComponent, 
      TooltipDirective, MatMenuModule, NgClass, DatePipe, TranslateModule, RoundedNumberPipe, FileNamePipe, FileExtensionPipe]
})
export class CloudStorageDialogComponent extends BaseFileBrowserComponent implements OnInit, OnDestroy, IModalWindow, IGetClass {
  @Input() ok: () => void;
  @Input() no: () => void;
  @Output() cancelPressed: EventEmitter<void> = new EventEmitter();
  @Output() okPressed: EventEmitter<void> = new EventEmitter();

  CloudFoldersEnum: typeof CloudFoldersEnum = CloudFoldersEnum;
  clouds: Array<CloudServiceEnum> = [CloudServiceEnum.GoogleDrive, CloudServiceEnum.DropBox, CloudServiceEnum.OneDrive];
  examPropertiesTabNames = ExamPropertiesTabNames;

  // private currentFilename: string = '';
  // private chosenFilename: string = '';

  cloudSortingParams = CloudSortingParam;

  private audiomakerFile: File | null = null;
  private settings: ISettings | null = null;

  // TODO maybe we need to refactor the cloud dialog?
  private isOptionsUsed = false;

  private get currentFolder(): CloudData | null {
    return this.cloudManager.currentFolder;
  }

  private get translatedTitle(): string {
    return this.cloudManager.translatedTabTitle;
  }

  private get selectDrive(): string {
    return this.translateSvc.instant('OPENSAVEDLG.SELECTDRIVE');
  }

  cloudFolders: Signal<Array<CloudFoldersEnum>> = computed(() => {
    if (this.dialogStatus() === SaveStatusEnum.Open) {
      return this.folders;
    }

    return this.folders.filter(f => f !== CloudFoldersEnum.Recent);
  });

  readOnlyInput: Signal<boolean> = computed(() => {
    const currentStatus = this.dialogStatus();

    return currentStatus === SaveStatusEnum.AudioSave || 
      currentStatus === SaveStatusEnum.Open ||
      currentStatus === SaveStatusEnum.ExamCreationFilePicker ||
      currentStatus === SaveStatusEnum.ExamModeFilePicker ||
      currentStatus === SaveStatusEnum.CompletedExamModeFileSave
  });

  cancelDialog: Signal<boolean> = computed(() => {
    const currentStatus = this.dialogStatus();
    return currentStatus !== SaveStatusEnum.AudioSave &&
      currentStatus !== SaveStatusEnum.ExamCreationFilePicker &&
      currentStatus !== SaveStatusEnum.ExamModeFilePicker &&
      currentStatus !== SaveStatusEnum.CompletedExamModeFileSave
  });

  filename: FilenameHelper;

  get cloudSortingData(): CloudSortingData {
    return this.cloudManager.cloudSortingData;
  }

  constructor(
    cloudManager: CloudDlgManagerService,
    settingsService: SettingsService,
    formBuilder: FormBuilder,
    dialog: MatDialog,
    private translateSvc: TranslateService,
    private fileCopySvc: FileCopyService,
    private audiomakerService: AudioMakerService,
    cloudSettingsService: CloudSettingsService,
    private cloudRecentFilesService: CloudRecentFilesService,
    private cloudFavoriteFilesService: CloudFavoriteFilesService,
    private paneService: PaneService,
    private readonly extraDocumentsService: ExtraDocumentsRestrictionService,
    private readonly toastrService: ToastrDialogService,
  ) {
    super(cloudManager, settingsService, formBuilder, dialog, cloudSettingsService);
  }

  parseDate(dateStr: string): Date {
    return new Date(dateStr);
  }

  getClass(): string {
    return 'cloud-storage-dlg';
  }

  getWindowType(): Type<IModalWindow> {
    return CloudStorageDialogComponent;
  }

  getWindowEnumType(): ModalWindowsTypes {
    return ModalWindowsTypes.CLOUD_STORAGE_DIALOG;
  }

  getInstanceOf(type: string): IModalWindow | IGetClass | ISetOptions | null {
    switch (type) {
      case 'IModalWindow':
        return this as unknown as IModalWindow;
      case 'IGetClass':
        return this as unknown as IGetClass;
      case 'ISetOptions':
        return this as unknown as ISetOptions;
      default: return null;
    }
  }

  // TODO: specify proper type here
  setOptions(options: CloudStorageDialogOptions): void {
    if (options) {
      // needed, because dialogStatus has default value
      this.isOptionsUsed = true;
      this.dialogStatus.set(options.dialogStatus);
      this.showAllFiles = options.showAllFiles || false;

      if (options.file) {
        this.audiomakerFile = options.file;
        this.filename = new FilenameHelper(this.audiomakerFile.name);
      }
    }
  }

  ngOnInit(): void {
    // if dialogStatus was not set in the options, retrieve it from the manager (from file-manager-dialog)
    if (!this.isOptionsUsed) {
      this.dialogStatus.set(this.cloudManager.getPickerStatus());
    }
    this.settings = this.settingsService.settings;
    // see options
    if (!this.filename) {
      this.filename = new FilenameHelper();
    }
    this.checkCurrentCloud();
    this.init();
    this.createForm();
    this.onValueChanges();
    this.subForSettings();
    // this.refreshListSub();
  }

  createForm(): void {
    const currentCloud = this.checkForNoSettings();
    this.formGroup = this.formBuilder.group({
      cloudStorage: [currentCloud],
      supportedControl: [this.showAllFiles],
      inputControl: [this.filename.name], //[this.dialogStatus === SaveStatusEnum.AudioSave && this.audiomakerFile !== null ? this.audiomakerFile.name : this.currentFilename],
      cloudFolders: [this.folderListing]
    });
  }

  onValueChanges(): void {
    super.onValueChanges();

    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(async (e) => {
        if (e.supportedControl !== this.showAllFiles) {
          this.showAllFiles = e.supportedControl;
          this.fileList = await this.getFileList();
        }

        if (this.filename.name === '') {
          // this.chosenFilename = this.currentFilename;
          this.removeActive();
        }
      })
    );
    this.subscriptions.add(
      this.formGroup.controls.inputControl.valueChanges.subscribe(async (e) => {

        if (e) {
          this.filename.name = e;

          // const ext = this.getFilenameAndExtension().extension;
          // this.chosenFilename = e.inputControl;
          // this.chosenFilename += ext;
        }
      })
    );
    this.subscriptions.add(
      this.formGroup.controls.cloudStorage.valueChanges.subscribe(async (e) => {
        this.cloudManager.clearFolderHistory();
        this.selectedItem = null;

        const cloudSettings: ICloudSettings = {
          cloudStorage: e,
          cloudFolder: this.folderListing,
          recentPath: [],
        };

        const isConnected = this.cloudSettingsService.isCloudConnected(e);
        // console.log(isConnected, 'isConnected');
        if (!isConnected) {
          try {
            await this.cloudSettingsService.connectCloud(e);
          } catch (err) {
            // not authenticated
            // dont save that provider into settings
            // take first connected provied and save it
            // if no connected save default
            const cloud = this.cloudSettingsService.getFirstConnectedCloud();
            // console.log(cloud, 'cloud');
            cloudSettings.cloudStorage = cloud;
          }
        }

        this.settingsService.saveSettings({ cloudSettings });

        // filthy hack to circumvent the lack of updating mechanism when params inside translation strings are updated.
        // See: https://github.com/ngx-translate/core/issues/582
        if (this.settings) {
          this.translateSvc.use(this.settingsService.settings.UILanguageValue || '');
        }

        this.ngOnInit(); // reload component after selecting drive
      })
    );
  }

  deleteFile(): void {
    this.cloudManager.deleteFile(this.selectedItem);
  }

  cutOrCopyFile(isCopy: boolean): void {
    if (this.selectedItem === null) {
      return;
    }

    const fileCopyInfo: Partial<FileCopyInfo> = {
      filename: this.selectedItem.name,
      mimeType: this.selectedItem.mimeType,
      fileToCopy: this.selectedItem,
      copy: isCopy,
    };

    this.fileCopySvc.setFileCopyInfo(fileCopyInfo);

    this.cloudManager.showComponent(CurrentDialogEnum.FileMoveDlg);
  }

  selectItem(event: Event, file: CloudData): void {
    this.selectedItem = file;
    // if (file && file.mimeType !== MimeTypesEnum.Folder && file.mimeType !== MimeTypesEnum.SharedDrive) {
    //   // this.chosenFilename = file.name;
    // } else if (this.dialogStatus === SaveStatusEnum.Save || this.dialogStatus === SaveStatusEnum.AudioSave) {
    //   this.chosenFilename = this.chosenFilename || this.translatedTitle;
    // }

    if (this.dialogStatus() === SaveStatusEnum.Open) {
      this.filename.setFilename(file.name, file.mimeType);
    } else if (file && file.mimeType !== MimeTypesEnum.Folder && file.mimeType !== MimeTypesEnum.SharedDrive) {
      this.filename.replaceFileName(file.name);
    }

    this.toggleActive(event);
  }

  saveAsFile(): void {
    // if (this.chosenFilename === '') {
    //   this.chosenFilename = this.translatedTitle;
    // }

    this.cloudManager.saveAsDataBuilder(this.filename.fullname, this.currentFolder);
    // this.restoreCloudFolder();

    this.okPress();
  }

  saveAudiofile(): void {
    if (this.audiomakerFile === null) {
      return;
    }

    this.cloudManager.saveAudiofile(this.audiomakerFile, this.currentFolder);
    this.audiomakerService.toggleAudioMakerFunction();

    // this.restoreCloudFolder();
    this.okPress();
  }

  okPress(): void {
    this.okPressed.emit();
  }

  cancelPress(): void {
    this.cancelPressed.emit();
  }

  async openFolder(mimeType: MimeTypesEnum | undefined, callFromBreadcrumbs: boolean = false): Promise<void> {
    if (!mimeType) {
      return;
    }

    // When opening a folder from Favorites, change the dropdown box to MyFiles
    if (this.folderListing === CloudFoldersEnum.Favorites) {
      this.openingFavoriteFolder = true;
      this.formGroup.controls.cloudFolders.setValue(CloudFoldersEnum.Myfiles);
    }

    if (mimeType === MimeTypesEnum.Folder || mimeType === MimeTypesEnum.SharedDrive) {
      await this.openTheFolder(callFromBreadcrumbs);
    } else if (this.dialogStatus() === SaveStatusEnum.Open || this.dialogStatus() === SaveStatusEnum.Ocr) {
      // console.log('this.paneIndex', this.paneService.getActivePaneIndex());
      await this.openFile(this.dialogStatus(), this.paneService.getActivePaneIndex());
      this.okPress();
    } else if (this.dialogStatus() === SaveStatusEnum.ExamCreationFilePicker || this.dialogStatus() === SaveStatusEnum.ExamModeFilePicker) {
      this.examModeAdd();
    }
  }

  handleSortingEvent(cloudSortingData: CloudSortingData): void {
    this.cloudManager.cloudSortingData = cloudSortingData;
    this.fileList = this.cloudManager.sort(this.fileList);
  }

  async examModeAdd(): Promise<void> {
    if (this.selectedItem) {
      const item = this.selectedItem;
      const file = await this.cloudManager.getFileFromId(item.id);
      if (this.extraDocumentsService.isFileAllowed(file)) {

        // check if the user selected an exam or an additional document
        switch (this.dialogStatus()) {
          case SaveStatusEnum.ExamCreationFilePicker:
            // TODO refactor cloud-storage-dialog so it can return a file
            // check if the file was already added to the list of additional files
            if (this.extraDocumentsService.wasFileWithSameNameAlreadyAdded(file.name)) {
              await this.showWarningExistDocument(false);
              return;
            }

            this.extraDocumentsService.setExamFile(file);
            break;
          case SaveStatusEnum.ExamModeFilePicker:
            // TODO refactor cloud-storage-dialog so it can return a file
            // check if the file was already added to the list of additional files
            if (this.extraDocumentsService.wasFileWithSameNameAlreadyAdded(file.name)) {
              await this.showWarningExistDocument(false);
              return;
            }
            // check if a file with the same name was already added as exam
            if (this.extraDocumentsService.wasExamWithSameNameAlreadyAdded(file.name)) {
              await this.showWarningExistDocument(true);
              return;
            }

            const files = this.extraDocumentsService.loadAdditionalDocument();
            files.push(file);
            this.extraDocumentsService.saveAdditionalDocuments(files);
            break;
          default: break;
        }

        // reopen the exam properties component
        // this.ok();
        // NOTE 19/12/2024
        // uses file-manager-dialog.component now
        this.okPress();
        // NOTE 10/08/2022
        // exam properties dialog is reopened in the document-restrictions component
        // this.modalService.createModalWindow(ExamPropertiesComponent, { messageKey: ExamPropertiesTabNames.Document });
      } else {
        // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey: 'COMMONMESSAGEDIALOG.NOT_SUPPORTED' });
        this.toastrService.showToaster(ToastrActionEnum.Error, this.translateSvc.instant('COMMONMESSAGEDIALOG.NOT_SUPPORTED'));
      }
    }
  }

  examModeCancel(): void {
    this.cancelPress();
    // NOTE 10/08/2022
    // exam properties dialog is reopened in the document-restrictions component
    // this.modalService.createModalWindow(ExamPropertiesComponent, { messageKey: ExamPropertiesTabNames.Document });
  }

  completedExamModeSave(): void {
    // file from options is kept under 'audiomakerFile'
    if (!this.audiomakerFile) {
      console.error('No file was found to save the completed exam');
      this.no();
      return;
    }

    this.cloudManager.uploadToCloud(this.currentFolder, this.filename.fullname, this.audiomakerFile)
    .then(() => {
      this.ok();
    })
    .catch((error) => {
      console.error(`Failed to upload completed exam: ${error}`);
      this.no();
    });
  }

  completedExamModeCancel(): void {
    this.no();
  }

  private subForSettings(): void {
    this.subscriptions.add(
      this.settingsService.getSettingsObservable().subscribe((i: ISettings) => {
        this.settings = i;
        this.folderListing = i.cloudSettings.cloudFolder;
      })
    );
  }

  private async init(): Promise<void> {
    if (this.noCloudSelected()) {
      return;
    }

    this.cloudFavoriteFilesService.setCloudAccountIdOnStart(this.currentCloud);

    if (this.folderListing === CloudFoldersEnum.Recent &&
      (!(await this.cloudRecentFilesService.isRecentFilesStorageNotEmpty(this.currentCloud)) || this.dialogStatus() !== SaveStatusEnum.Open)) {
      this.formGroup.controls.cloudFolders.setValue(CloudFoldersEnum.Myfiles);
    }

    const storedFolderPath = this.settings?.cloudSettings.recentPath || null;

    if (this.dialogStatus() === SaveStatusEnum.Ocr) {
      this.openRootFolder(SaveStatusEnum.Ocr);
    } else if (this.folderListing === CloudFoldersEnum.Favorites && (storedFolderPath !== null && storedFolderPath.length === 0)) {
      this.openRootFolder(SaveStatusEnum.Open);
    }
    else if (storedFolderPath !== null && storedFolderPath.length > 0) {
      this.openStoredFolder(storedFolderPath[storedFolderPath.length - 1])
        .then(() => this.restoreFolderPath(storedFolderPath.map(x => { return { id: x }; })))
        .catch(() => this.openFolder(MimeTypesEnum.Folder));
    } else {
      this.openFolder(MimeTypesEnum.Folder);
    }

    if (this.dialogStatus() === SaveStatusEnum.Save) {
      // this.currentFilename = this.translatedTitle;
      this.filename = new FilenameHelper(this.translatedTitle);
      // EPUB files are saved as .alineabook with annotations
      if (this.filename.extension === '.epub') {
        this.filename = new FilenameHelper(this.filename.name + '.alineabook');
      }
    }

    if (this.dialogStatus() === SaveStatusEnum.Open) {
      this.filename = new FilenameHelper();
    }
  }

  private async openStoredFolder(folderId: string): Promise<void> {
    this.fileList = await this.cloudManager.openStoredFolder(folderId, this.dialogStatus());
  }

  private async restoreFolderPath(path: Partial<CloudData>[]): Promise<void> {
    this.cloudManager.restoreFolderPath(path, this.dialogStatus());
  }

  private async openFile(dialogStatus: SaveStatusEnum, paneIndex: number): Promise<void> {
    if (this.selectedItem === null) {
      return Promise.reject();
    }

    this.cloudManager.getFileFromCloud(this.selectedItem, dialogStatus, paneIndex);
  }

  private async openTheFolder(callFromBreadcrumbs: boolean = false): Promise<void> {
    if (!callFromBreadcrumbs && this.selectedItem) {
      this.cloudManager.addfolderToHistory(this.selectedItem);
    }
    this.fileList = await this.getFileList();
  }

  toggleFavorite() :void {
    this.cloudFavoriteFilesService.toggleFavoriteFolder(this.selectedItem, this.currentCloud);
  }

  isFavorite(item: CloudData): boolean {
    return this.cloudFavoriteFilesService.isFavorite(item, this.currentCloud);
  }

  // private refreshListSub(): void {
  //   this.subscriptions.add(
  //     this.cloudManager.RefreshList.subscribe(() => {
  //       this.ngOnInit();
  //     })
  //   );
  // }

  private checkForNoSettings(): string {
    if (this.settings && this.settings.cloudSettings && this.settings.cloudSettings.cloudStorage !== CloudServiceEnum.CloudStorage && this.cloudSettingsService.isCloudConnected(this.settings.cloudSettings.cloudStorage)) {
      return this.settings.cloudSettings.cloudStorage;
    } else {
      return this.selectDrive;
    }
  }

  // private selectFilename(): string {
  //   let filename = '';
  //   if (this.dialogStatus === SaveStatusEnum.AudioSave && this.audiomakerFile) {
  //     filename = this.audiomakerFile.name;
  //   } else if (!this.selectedItem) {
  //     filename = this.currentFilename;
  //   } else {
  //     if (this.dialogStatus === SaveStatusEnum.Open) {
  //       filename = this.selectedItem.name;
  //     } else if (this.dialogStatus === SaveStatusEnum.Save) {
  //       if (this.selectedItem.mimeType === MimeTypesEnum.Folder || this.selectedItem.mimeType === MimeTypesEnum.SharedDrive) {
  //         filename = this.chosenFilename ? this.chosenFilename : this.currentFilename;
  //       } else {
  //         filename = this.selectedItem.name;
  //       }
  //     }
  //   }

  //   return filename;
  // }


  // TODO refactor cloud-storage-dialog so it can return a file
  // copy-pasted this from document-restriction.component.ts
  private showWarningExistDocument(isExam: boolean): Promise<void> {
    const data: MessageDialogData = {
      title: '',
      message: isExam ? 'EXAM_RESTRICTIONS.DOCUMENT.EXAM_FILE_WARNING' : 'EXAM_RESTRICTIONS.DOCUMENT.ATTACHMENT_WARNING',
      actions: [
        {
          title: 'Ok',
          color: MessageDialogActionColor.Primary,
          handler: () => {}
        }
      ]
    };
    return this.dialog.open(MessageDialogComponent, { data }).afterClosed().toPromise();
  }
}
